import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { Popover, Tooltip } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { useUnit } from '@/framework';
import { useLocaleStore } from '@/i18n';
import { parseToUnit } from '@/utils/unit-kit';
import type { ISize } from '@/version-types';
import type { StageToolbar } from '../../stage-toolbar';
import { useStageToolbarState } from '../../states/stage-toolbar-state';
import { useScaleController } from './use-scale-controller';

const SCALE_LIST = [4, 3, 2, 1.75, 1.5, 1.25, 1, 0.75, 0.5, 0.25, 0.15];

interface IMenuScaleProps {
    active: boolean;
    pointerInsideToolbar: boolean;
    setActive(active: boolean): void;
    showCanvasSize?: boolean;
    toolbar: StageToolbar;
}

export const MenuScale = ({ active, pointerInsideToolbar, setActive, showCanvasSize, toolbar }: IMenuScaleProps) => {
    const { $t } = useLocaleStore();
    const { enableAutoScale, framework, scale, setScale, zoomIn, zoomOut } = useScaleController(toolbar);
    const unit = useUnit();
    const { events, frame } = framework.service;
    const [canvasSize, setCanvasSize] = useState<ISize>(frame.getSize());
    const [expanded, setExpanded] = useState(false);
    const hasActiveItem = useStageToolbarState()((state) => state.activeItemKey !== null);
    const disabledAutoZoomInAndOut = useMemo(() => !!window.isIntl, []);

    useEffect(() => {
        const handlers = [
            frame.onResize(() => setCanvasSize(frame.getSize())),
            frame.onActiveIndexChange(() => setCanvasSize(frame.getSize())),
            frame.onRenderComplete(() => setCanvasSize(frame.getSize())),
        ];
        return () => handlers.forEach((handler) => events.off(handler));
    }, [events, frame]);

    useEffect(() => {
        if (!pointerInsideToolbar && !hasActiveItem) setExpanded(false);
    }, [hasActiveItem, pointerInsideToolbar]);

    const canvasSizeText = `${parseToUnit(canvasSize.width, unit)} x ${parseToUnit(canvasSize.height, unit)}${unit}`;

    return (
        <div
            className={classNames('stage-toolbar-scale-menu', {
                open: expanded || disabledAutoZoomInAndOut,
            })}
            onMouseDown={(event) => event.stopPropagation()}
            onMouseEnter={() => setExpanded(true)}
        >
            <Tooltip
                placement="top"
                title={
                    scale <= SCALE_LIST[SCALE_LIST.length - 1]
                        ? $t((value) => value.navigatorLayer.minCanvas)
                        : $t((value) => value.navigatorLayer.shrink)
                }
                trigger="hover"
            >
                <span
                    className="zoomInAndOut"
                    onClick={() => {
                        setActive(false);
                        zoomOut();
                    }}
                >
                    <IconFont name="MinusBlack" fontSize={13} />
                </span>
            </Tooltip>

            <Tooltip
                placement="top"
                title={
                    showCanvasSize ? `${$t((value) => value.navigatorLayer.canvasSize)}：${canvasSizeText}` : undefined
                }
                trigger="hover"
            >
                <Popover
                    open={active}
                    onOpenChange={setActive}
                    content={
                        <div className="l-navigator__navigator-menu">
                            <ul>
                                {SCALE_LIST.map((value) => (
                                    <li
                                        key={value}
                                        className={value === scale ? 'active' : ''}
                                        onClick={() => {
                                            setScale(value);
                                        }}
                                    >
                                        {~~(value * 100)}%
                                    </li>
                                ))}
                                <li
                                    onClick={() => {
                                        enableAutoScale();
                                    }}
                                >
                                    {$t((value) => value.navigatorLayer.adaptScale)}
                                </li>
                                <li
                                    onClick={() => {
                                        setScale(1);
                                    }}
                                >
                                    {$t((value) => value.navigatorLayer.realSize)}
                                </li>
                            </ul>
                        </div>
                    }
                    trigger="click"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                    placement="topLeft"
                    overlayClassName="app-operate-scale-list"
                >
                    <span className="scale-label">{`${Math.round(scale * 100)}%`}</span>
                </Popover>
            </Tooltip>

            <Tooltip
                placement="top"
                title={
                    scale >= SCALE_LIST[0]
                        ? $t((value) => value.navigatorLayer.maxCanvas)
                        : $t((value) => value.navigatorLayer.enlarge)
                }
                trigger="hover"
            >
                <span
                    className="zoomInAndOut"
                    onClick={() => {
                        setActive(false);
                        zoomIn();
                    }}
                >
                    <IconFont name="PlusBlack" fontSize={13} />
                </span>
            </Tooltip>
        </div>
    );
};
